import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { DurationInputComponent } from 'src/app/base/shared/duration-input/duration-input.component';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from 'src/app/base/services/toast.service';

@Component({
  selector: 'app-fast-checkin-time-settings',
  templateUrl: './fast-checkin-time-settings.component.html',
  styleUrls: ['./fast-checkin-time-settings.component.scss'],
  imports: [TranslateModule, ReactiveFormsModule, DurationInputComponent]
})
export class FastCheckinTimeSettingsComponent implements OnInit, OnChanges {
  @Input() public startTime: Date = new Date();
  @Input() public durationMinutes: number = 60;
  @Input() public maxDurationMinutes: number = 1440;
  @Input() public minDurationMinutes: number = 5;
  @Input() public translationPrefix: string = 'i18n.domain.fast-checkin';

  @Output() public durationMinutesChange: EventEmitter<number> = new EventEmitter<number>();

  public durationControl: FormControl<number> = new FormControl<number>(60, {
    nonNullable: true,
    validators: [Validators.required]
  });

  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);

  ngOnInit() {
    this.durationControl.setValue(this.clampDuration(this.durationMinutes), { emitEvent: false });
    this.durationControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const clampedDuration = this.clampDuration(value);
        if (clampedDuration !== value) {
          this.durationControl.setValue(clampedDuration, { emitEvent: false });
        }
        this.durationMinutesChange.emit(clampedDuration);
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['durationMinutes'] && this.durationControl && this.durationControl.value !== this.durationMinutes) {
      this.durationControl.setValue(this.clampDuration(this.durationMinutes), { emitEvent: false });
    }
  }

  public addMinutesToDuration(minutes: number) {
    const currentDuration = this.durationControl.value;
    const requestedDuration = currentDuration + minutes;
    const newDuration = this.clampDuration(requestedDuration);

    if (minutes > 0 && newDuration === this.maxDurationMinutes) {
      this.showMaxDurationReachedToast();
    }

    this.durationControl.setValue(newDuration);
  }

  public getEndTime(): string {
    const endTime = new Date(this.startTime.getTime() + this.durationControl.value * 60000);
    return endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  public getStartTime(): string {
    return this.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  }

  private clampDuration(duration: number): number {
    if (duration < this.minDurationMinutes) {
      return this.minDurationMinutes;
    }

    if (duration > this.maxDurationMinutes) {
      return this.maxDurationMinutes;
    }

    return duration;
  }

  private showMaxDurationReachedToast(): void {
    this.toastService.showWarning(
      'i18n.domain.fast-checkin.fast-checkin-time-settings.max-parking-duration',
      'i18n.domain.fast-checkin.fast-checkin-time-settings.max-parking-duration-reached',
    );
  }
}
